import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RealTimeTrendChart = () => {
  const [chartData, setChartData] = useState([]);
  const [timeRange, setTimeRange] = useState('1h'); // 1h, 4h, 12h, 24h
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    // Generate initial data based on time range
    const generateInitialData = () => {
      const now = new Date();
      const intervals = {
        '1h': { count: 12, interval: 5 * 60 * 1000 }, // 5-minute intervals
        '4h': { count: 16, interval: 15 * 60 * 1000 }, // 15-minute intervals
        '12h': { count: 24, interval: 30 * 60 * 1000 }, // 30-minute intervals
        '24h': { count: 24, interval: 60 * 60 * 1000 } // 1-hour intervals
      };

      const { count, interval } = intervals[timeRange];
      const data = [];

      for (let i = count - 1; i >= 0; i--) {
        const timestamp = new Date(now.getTime() - (i * interval));
        const baseValue = 35000 + Math.sin(i * 0.3) * 10000;
        const noise = (Math.random() - 0.5) * 5000;
        
        data.push({
          time: timestamp,
          timeLabel: timestamp.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit',
            ...(timeRange === '24h' && { hour12: false })
          }),
          sales: Math.max(0, baseValue + noise),
          collections: Math.max(0, (baseValue + noise) * 0.85 + (Math.random() - 0.5) * 3000),
          predicted: baseValue + noise + 2000 + Math.sin(i * 0.2) * 1500
        });
      }

      return data;
    };

    setChartData(generateInitialData());
  }, [timeRange]);

  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      const now = new Date();
      const baseValue = 35000 + Math.sin(Date.now() * 0.0001) * 10000;
      const noise = (Math.random() - 0.5) * 5000;
      
      const newDataPoint = {
        time: now,
        timeLabel: now.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          ...(timeRange === '24h' && { hour12: false })
        }),
        sales: Math.max(0, baseValue + noise),
        collections: Math.max(0, (baseValue + noise) * 0.85 + (Math.random() - 0.5) * 3000),
        predicted: baseValue + noise + 2000 + Math.sin(Date.now() * 0.0002) * 1500
      };

      setChartData(prev => {
        const updated = [...prev.slice(1), newDataPoint];
        return updated;
      });
      
      setLastUpdate(now);
    }, 15000); // Update every 15 seconds

    return () => clearInterval(interval);
  }, [isLive, timeRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-muted-foreground capitalize">
                  {entry.dataKey}
                </span>
              </div>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const timeRangeOptions = [
    { value: '1h', label: '1H' },
    { value: '4h', label: '4H' },
    { value: '12h', label: '12H' },
    { value: '24h', label: '24H' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Chart Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Real-Time Sales Trends
            </h3>
          </div>
          
          {/* Live Indicator */}
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
            <span className={`text-xs font-medium ${isLive ? 'text-success' : 'text-muted-foreground'}`}>
              {isLive ? 'LIVE' : 'PAUSED'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Time Range Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {timeRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTimeRange(option.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-150 ${
                  timeRange === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Live Toggle */}
          <Button
            variant={isLive ? "default" : "outline"}
            size="sm"
            onClick={() => setIsLive(!isLive)}
          >
            <Icon name={isLive ? "Pause" : "Play"} size={14} />
            {isLive ? 'Pause' : 'Resume'}
          </Button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="timeLabel" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Sales Line */}
            <Line
              type="monotone"
              dataKey="sales"
              stroke="var(--color-primary)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, stroke: 'var(--color-primary)', strokeWidth: 2 }}
            />
            
            {/* Collections Line */}
            <Line
              type="monotone"
              dataKey="collections"
              stroke="var(--color-success)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, stroke: 'var(--color-success)', strokeWidth: 2 }}
            />
            
            {/* Prediction Line */}
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="var(--color-warning)"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4, stroke: 'var(--color-warning)', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Chart Legend */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full" />
            <span className="text-sm text-muted-foreground">Sales</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full" />
            <span className="text-sm text-muted-foreground">Collections</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-warning rounded-full" />
            <span className="text-sm text-muted-foreground">Predicted</span>
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default RealTimeTrendChart;